<template>
  <div class="card">
    <vue-easy-lightbox
        moveDisabled
        :visible="isLightboxVisible"
        :imgs="lightBoxImageUrl"
        @hide="isLightboxVisible = false"
    ></vue-easy-lightbox>
    <div class="card-body">
      <!-- #region Request and Refresh buttons -->
      <div class="d-flex align-items-center py-2">
        <div>
          <h5>{{ eloadB2BRequestsTotal }} Record(s)</h5>
        </div>
        <div class="ms-auto">
          <router-link :to="`form/0`" v-if="userAccess.access.create">
            <button class="btn btn-primary me-2 mb-2"><i class="uil uil-plus me-1"></i>Request Fund</button>
          </router-link>
          <button class="btn btn-secondary mb-2" @click="getRequests()">
            <feather type="refresh-ccw" size="16" class="icon nav-icon"></feather>&nbsp;&nbsp; Refresh
          </button>
        </div>
      </div>
      <!-- #endregion Request and Refresh buttons -->

      <!-- #region Search and Filter -->
      <div class="row p-2">
        <div class="col-md-4 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Search Keyword">
              <form class="d-flex" @submit.prevent="">
                <b-form-group id="input-group-3" label-for="search-field">
                  <b-form-select
                    class="form-select rounded-0 rounded-start shadow-none"
                    id="search-field"
                    v-model="search.field"
                    :options="searchFieldOptions"
                  ></b-form-select>
                </b-form-group>

                <b-input-group>
                  <b-form-input class="rounded-0 rounded-end" v-model="search.keyword" @keyup.enter="getRequests()"></b-form-input>
                </b-input-group>
              </form>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Filter by Business Name">
              <b-form-select
                class="form-select rounded shadow-none dd-custom-size"
                v-model="search.businessName"
                :options="businessNameOptions"
              ></b-form-select>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Filter by Status">
              <b-form-select
                class="form-select rounded shadow-none dd-custom-size"
                v-model="search.status"
                :options="statusOptions"
              ></b-form-select>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Filter by Payment Method">
              <b-form-select
                class="form-select rounded shadow-none dd-custom-size"
                v-model="search.paymentMethod"
                :options="paymentMethodOptions"
              ></b-form-select>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Filter by Requested Date" class="me-2 w-daterange">
              <flat-pickr
                v-model="search.dateRange"
                :config="rangeOptions"
                placeholder="Select a date range"
                class="form-control"
              ></flat-pickr>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-4 col-sm-12 d-flex">
          <div class="me-2">
            <b-button variant="primary" class="h-fit d-flex align-items-center justify-content-center me-2 w-fit" @click="getRequests">
              <i class="bx bx-search me-1 mt-1"></i>Search
            </b-button>
          </div>
          <div class="">
            <b-button
              variant="primary"
              class="h-fit me-2 d-flex align-items-center justify-content-center w-fit"
              @click="
                isSearching = true;
                onExport();
              "
            >
              <i class="uil uil-export me-1"></i>
              Export
            </b-button>
          </div>
        </div>
      </div>
      <!-- #endregion Search and Filter -->

      <!-- #region Table -->
      <div class="table-responsive">
        <div class="table align-middle table-nowrap">
          <b-table
            :items="eloadB2BRequests"
            :fields="fields"
            responsive="sm"
            :current-page="currentPage"
            :sort-by.sync="sortByField"
            :sort-desc.sync="sortDesc"
            :filter-included-fields="filterOn"
            class="table-check"
            @sort-changed="sortList"
            tbody-tr-class="align-middle"
          >
            <template #cell(action)="data">
              <!-- TODO: Add checking if approver -->

              <b2b-action
               view = "list"
               :requestData = data.item
               :userAccess = userAccess.access
               @reload = "getRequests()"
              />
            </template>

            <template #cell(id)="data">
              <router-link :to="`details/${data.item.payment_method === 'qrph' && !data.item.reference_number ? data.item.id : data.item.reference_number}`">
                {{ formatRequestId(data.item) }}
              </router-link>
              <div>{{ data.item.created_at | formatDate }}</div>
            </template>

            <template #cell(reference_number)="data">
              <div v-if="data.item.reference_number || data.item.sender_bank_name">
                <div>{{ data.item.reference_number | notEmpty }}</div>
                <div v-if="data.item.sender_bank_name">{{ data.item.sender_bank_name }}</div>
              </div>
              <div v-else>--</div>
            </template>

            <template #cell(user_id)="data">
              <div>{{ data.item.user_id | notEmpty }}</div>
              <div>{{ data.item.name | notEmpty }}</div>
            </template>

            <template #cell(status)="data">
              <div
                :class="{
                  'text-success': data.item.status == 'approved',
                  'requesting-color-code': data.item.status == 'requesting',
                  'text-muted': data.item.status == 'rejected',
                  'text-warning': data.item.status == 'for_payment',
                }"
                :style="data.item.status == 'expired_qrph' ? { color: '#831118' } : {}"
              >
                {{ setStatus(data.item.status) }}
              </div>
            </template>

            <template #cell(payment_method)="data">
              <!-- <span v-if="data.item.payment_method === 'qrph' && data.item.status === 'for_payment'">
                <b-button variant="primary" size="sm" @click="viewImage(data.item.qrph_img)">View QRPH</b-button>
              </span> -->
              <span>{{ setPaymentMethod(data.item.payment_method, data.item.is_ewt) }}</span>
            </template>

            <template #cell(wth)="data">
              <div v-if="data.item.is_ewt == 1 && data.item.ewt_amount">
                PHP {{ data.item.ewt_amount | numberWithCommas }}
              </div>
              <div v-else>--</div>
            </template>

            <template #cell(service_charge)="data">
              <div v-if="data.item.service_charge">PHP {{ data.item.service_charge | numberWithCommas }}</div>
              <div v-else>--</div>
            </template>

            <template #cell(ewt_file)="data">
              <a v-if="data.item.is_ewt == 1 && data.item.ewt_file"
                :href="data.item.ewt_file" target="_blank" class="text-decoration-underline" style="color: #3967B8;">
                View BIR 2307
              </a>
              <span v-else>--</span>
            </template>

            <template #cell(deposit_ss_url)="data">
              <b-img v-if="data.item.deposit_ss_url"
                @click="viewImage(data.item.deposit_ss_url)"
                :src="data.item.deposit_ss_url"
                @error="(e) => (e.target.src = require('@/assets/images/img-placeholder.png'))"
                alt=""
                thumbnail
                height="50"
                width="50"
                class="c-pointer"
              >
              </b-img>
              <span v-else>--</span>
            </template>

            <template #cell(business_name)="data">
              <div>{{ data.item.business_name | notEmpty }}</div>
            </template>

            <template #cell(deposit_date)="data">
              <div v-if="data.item.payment_method === 'qrph' && data.item.status !== 'approved'">--</div>
              <div v-else-if="data.item.payment_method === 'qrph' && data.item.status === 'approved'">{{ data.item.approved_at | formatDateOnly | notEmpty }}</div>
              <div v-else>{{ data.item.deposit_date | formatDateOnly | notEmpty }}</div>
            </template>

            <template #cell(deposit_amount)="data">
              <div>{{ data.item.deposit_amount ? `PHP ${$options.filters.numberWithCommas(data.item.deposit_amount)}` : '--' }}</div>
            </template>

            <template #cell(rebate_percentage)="data">
              <div>{{ data.item.rebate_percentage != null ? `${data.item.rebate_percentage}%` : '--' }}</div>
            </template>

            <template #cell(topup_amount)="data">
              <div>{{ data.item.topup_amount ? `PHP ${$options.filters.numberWithCommas(data.item.topup_amount)}` : '--' }}</div>
            </template>

            <template #cell(requested_by)="data">
              <div>{{ data.item.requested_by | notEmpty }}</div>
            </template>

            <template #cell(approved_by)="data">
              <div>{{ data.item.approved_by | notEmpty }}</div>
            </template>

            <template #cell(approved_at)="data">
              <div>{{ data.item.approved_at | formatDate | notEmpty }}</div>
            </template>

            <template #cell(remarks_reason)="data">
              <div v-if="data.item.remarks_reason" v-b-tooltip.hover :title="data.item.remarks_reason">
                {{ data.item.remarks_reason | truncator(20,'...') }}
              </div>
              <div v-else>--</div>
            </template>

            <template #cell(remarks)="data">
              <div v-if="data.item.status == 'rejected' && data.item.remarks" v-b-tooltip.hover :title="data.item.remarks">
                {{ data.item.remarks | truncator(20,'...') }}
              </div>
              <div v-else>--</div>
            </template>

            <template #cell(proof_of_confirmation)="data">
              <div v-if="data.item.status == 'approved' && data.item.proof_of_confirmation" class="custom-image-container">
                <img 
                  :src="data.item.proof_of_confirmation" 
                  @click="viewImage(data.item.proof_of_confirmation)" 
                  alt="Proof of confirmation" 
                  class="c-pointer"
                  @error="(e) => e.target.src = require('@/assets/images/img-placeholder.png')"
                >
              </div>
              <div v-else>--</div>
            </template>
          </b-table>
        </div>
      </div>
      <div class="row g-0 text-center" v-if="eloadB2BRequestsTotal == 0">
        <span>No data to display.</span>
      </div>
      <!-- #endregion Table -->

      <!-- #region Pagination -->
      <div class="row g-0 text-center text-sm-start" v-if="eloadB2BRequestsTotal != 0 && eloadB2BRequestsTotal > 10">
        <div class="col-sm-12">
          <div class="row">
            <div class="col">
              <div class="form-group row pull-left">
                <label for="staticEmail" class="col-sm-6 col-form-label">Display:</label>
                <div class="col-sm-6">
                  <b-form-group label-for="display-record">
                    <b-form-select
                      class="form-select shadow-none w-fit"
                      id="display-record"
                      v-model="perPage"
                      :options="pageOptions"
                      @change="getRequests()"
                    ></b-form-select>
                  </b-form-group>
                </div>
              </div>
              <div class="dataTables_paginate paging_simple_numbers float-end" v-if="eloadB2BRequestsTotal > perPage">
                <b-pagination
                  class="pagination-rounded"
                  v-model="currentPage"
                  @input="getRequests()"
                  :total-rows="eloadB2BRequestsTotal"
                  :per-page="perPage"
                ></b-pagination>
              </div>
            </div>
          </div>
        </div>
        <!-- end col -->
      </div>
      <!-- #endregion Pagination -->
    </div>
  </div>
</template>

<script>
import { layoutMethods, gbitsEloadTopupMethods, gbitsEloadTopupComputed, exportMethods } from "@/state/helpers";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Swal from "sweetalert2";
import b2bAction from '@/views/pages/_version_2_/gbits/topup-request/action.vue';

export default {
  page: {
    title: "ELoad B2B Requests Dashboard",
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      filter: null,
      filterOn: [],
      orderBy: "t.id",
      sortByField: "id",
      sort: "DESC",
      sortDesc: true,
      submitted: false,
      search: {
        keyword: "",
        field: "",
        businessName: "",
        status: "",
        paymentMethod: "",
        dateRange: null,
      },
      rangeOptions: {
        mode: "range",
      },
      searchFieldOptions: [
        { text: "Search All", value: "" },
        { text: "Request ID", value: "t.id" },
        { text: "Reference Number", value: "t.reference_number" },
        { text: "Account ID", value: "t.account_id" },
        { text: "Name", value: "u.first_name" },
        { text: "Business Name", value: "b.business_name" },
        { text: "Requested By", value: "t.requested_by" },
        { text: "Approved By", value: "t.approved_by" },
      ],
      statusOptions: [
        { text: "All", value: "" },
        { text: "Requesting", value: "requesting" },
        { text: "Top Up Completed", value: "approved" },
        { text: "Rejected", value: "rejected" },
        { text: "For Payment", value: "for_payment" },
        { text: "Expired QR", value: "expired_qrph" },
      ],
      paymentMethodOptions: [
        { text: "All", value: "" },
        { text: "Bank Deposit", value: "bank_deposit" },
        { text: "QRPH", value: "qrph" },
        { text: "EWT", value: "ewt" },
      ],
      fields: [
        { key: "action", sortable: false },
        { key: "id", label: "Request ID & Date", sortable: true },
        { key: "reference_number", label: "Reference Number & Sender Bank", sortable: true },
        { key: "user_id", label: "Account ID & Name", sortable: true },
        { key: "status", sortable: true },
        { key: "deposit_amount", label: "Deposit Amount", sortable: true },
        { key: "topup_amount", label: "Wallet Top Up Amount", sortable: true },
        { key: "payment_method", label: "Payment Method", sortable: true },
        { key: "deposit_ss_url", label: "Deposit Screenshot", sortable: false },
        { key: "business_name", sortable: true },
        { key: "deposit_date", sortable: true },
        { key: "wth", label: "WHT Amount", sortable: true },
        { key: "ewt_file", label: "BIR 2307 Form", sortable: false },
        { key: "service_charge", label: "Convenience Fee", sortable: true },
        { key: "requested_by", sortable: true },
        { key: "rebate_percentage", label: "Default Rebate", sortable: true },
        { key: "approved_by", label: "Review By Finance", sortable: true },
        { key: "approved_at", label: "Review Date", sortable: true },
        { key: "remarks_reason", label: "Remarks Purpose", sortable: false },
        { key: "remarks", label: "Reject Reason", sortable: false },
        { key: "proof_of_confirmation", label: "Proof of Confirmation", sortable: false },
      ],
      userAccess: {
        access: {
          create: false,
          read: false,
          update: false,
          delete: false,
        }
      },
      isLightboxVisible: false,
      lightBoxImageUrl: "",
      showDepositImg: false,
      imgsRef: null,
    };
  },
  components: {
    flatPickr,
    b2bAction,
  },
  computed: {
    ...gbitsEloadTopupComputed,
    businessNameOptions() {
      const options = [{ text: "All", value: "" }];
      if (this.eloadB2BBusinessNames && this.eloadB2BBusinessNames.length) {
        this.eloadB2BBusinessNames.forEach(item => {
          options.push({ text: item.business_name, value: item.account_id });
        });
      }
      return options;
    },
  },
  methods: {
    ...layoutMethods,
    ...exportMethods,
    ...gbitsEloadTopupMethods,
    async getRequests() {
      let params = {
        page: this.isSearching ? 1 : this.currentPage,
        rows: this.perPage,
        sort: this.sort,
        orderBy: this.orderBy,
        search: this.search,
      };

      await this.getEloadB2BRequests(params).then(() => {
        this.isSearching = false;
      });
    },
    setStatus(status) {
      switch (status) {
        case "requesting": return "Requesting";
        case "approved": return "Top Up Completed";
        case "rejected": return "Rejected";
        case "for_payment": return "For Payment";
        case "expired_qrph": return "Expired QR";
        default: return "";
      }
    },
    setPaymentMethod(method, isEwt) {
      if (method === "bank_deposit") return "Bank Deposit";
      if (method === "qrph") return "QRPH";
      if (method === "ewt" || isEwt == 1) return "EWT";
      return "--";
    },
    formatRequestId(item) {
      if (!item.deposit_date && !item.created_at) return item.id;
      const date = item.deposit_date || item.created_at;
      const d = new Date(date);
      const yyyyMM = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
      const paddedId = String(item.id).padStart(5, '0');
      return `${yyyyMM}${paddedId}`;
    },
    async sortList(action) {
      var column = "";
      switch (action.sortBy) {
        case "name":
          column = "u.first_name";
          break;
        case "mobile_number":
          column = "u.mobile_number";
          break;
        case "business_name":
          column = "b.business_name";
          break;
        case "user_id":
          column = "t.account_id";
          break;
        case "deposit_amount":
          column = "t.paid_amount";
          break;
        default:
          column = `t.${action.sortBy}`
          break;
      }

      this.orderBy = column;
      this.sort = action.sortDesc ? "DESC" : "ASC";
      await this.getRequests();
    },
    async onExport() {
      let params = {
        module: "gbits_topup_requests",
        email: JSON.parse(localStorage.getItem("userData")).email,
        search: this.search,
        sort: this.sort,
        orderBy: this.orderBy,
      };

      this.exportReportExt(params)
        .then(() => Swal.fire("Success!", `Your request for report is pending. It will be sent to your email once it's ready.`, "success"))
        .catch(() => Swal.fire("Failed!", `Your request for reports has failed.`, "warning"));
    },
    viewImage(img){
        this.lightBoxImageUrl = img
        this.isLightboxVisible = true;
    }
  },
  async mounted() {
    this.changeTitle({ title: "Topup Request Approval" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: "Peddlr", to: { name: "Home" } },
        { text: "GBITS Manager", to: { name: "" } },
        { text: "Request", to: { name: "" } },
      ],
    });

    await this.getRequests();
    await this.getTopupAccess()
      .then((access) => this.userAccess.access = access)
      .catch(() => {});
    await this.getBusinessNames().catch(() => {});
  },
};
</script>

<style scoped>
.requesting-color-code {
  color: #dc7512 !important;
}

.img-thumbnail {
    max-height: 50px !important;
}

.custom-image-container {
    width: 60pt;
    height: 30px;
    overflow: hidden;
    border: 1px solid #999;
    border-radius: 8px;
}
.custom-image-container img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
</style>
